/**
 * Withdraw: asset → saved (whitelisted) address → amount → review → 2FA code.
 * The API re-checks everything (limits, whitelist, cooldown, balance); this screen just makes the
 * rules visible and catches obvious mistakes early. Amounts stay decimal strings throughout.
 */
import type { WithdrawalInfo } from "@custos/api-client";
import {
  Address,
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Modal,
  Notice,
  Select,
  addDecimalStrings,
  buttonClasses,
  compareDecimalStrings,
  formatAmount,
  isZeroAmount,
  normaliseAmountInput,
  subtractDecimalStrings,
  cn,
  SuccessCheck,
} from "@custos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { AddAddressModal } from "../components/AddAddressModal";
import { PageHeader } from "../components/AppShell";
import { Amount, CodeInput, Row } from "../components/bits";
import { withdrawalStatus } from "../lib/activity";
import { api } from "../lib/api";
import { assetKey, findAsset, findChain, networkLabel } from "../lib/chains";
import { errorMessage } from "../lib/errors";
import { qk, useBalances, useConfig, useWhitelist } from "../lib/queries";

export function WithdrawPage() {
  const config = useConfig();
  const balances = useBalances();
  const whitelist = useWhitelist();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const assets = config.data?.assets ?? [];
  const chains = config.data?.chains ?? [];
  const initial =
    params.get("chain") && params.get("asset") ? assetKey(params.get("chain")!, params.get("asset")!) : "";
  const [selectedKey, setSelectedKey] = useState(initial);
  const [targetId, setTargetId] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [touched, setTouched] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [result, setResult] = useState<WithdrawalInfo | null>(null);

  // Default to the first asset with a balance.
  useEffect(() => {
    if (selectedKey || !balances.data) return;
    const first = balances.data.find((b) => !isZeroAmount(b.balance)) ?? balances.data[0];
    if (first) setSelectedKey(assetKey(first.chain, first.asset));
  }, [balances.data, selectedKey]);

  const [chainKey = "", symbol = ""] = selectedKey.split(":");
  const asset = findAsset(assets, chainKey, symbol);
  const chain = findChain(chains, chainKey);
  const available = balances.data?.find((b) => b.chain === chainKey && b.asset === symbol)?.balance ?? "0";
  const targets = useMemo(
    () => (whitelist.data ?? []).filter((w) => w.chain === chainKey),
    [whitelist.data, chainKey],
  );
  const target = targets.find((t) => t.id === targetId);

  useEffect(() => {
    const usable = targets.filter((t) => t.usable);
    if (!usable.some((t) => t.id === targetId)) setTargetId(usable[0]?.id ?? "");
  }, [targets, targetId]);

  const amount = asset ? normaliseAmountInput(amountInput, asset.decimals) : null;
  const fee = asset?.withdrawalFee ?? "0";
  const total = amount ? addDecimalStrings(amount, fee) : null;
  const max = subtractDecimalStrings(available, fee);

  let amountError: string | null = null;
  if (touched && asset) {
    if (!amountInput.trim()) amountError = "Enter an amount.";
    else if (!amount) amountError = `Use a number with at most ${asset.decimals} decimal places.`;
    else if (compareDecimalStrings(amount, asset.minWithdrawal) < 0)
      amountError = `The minimum is ${asset.minWithdrawal} ${asset.symbol}.`;
    else if (total && compareDecimalStrings(total, available) > 0)
      amountError = "Not enough balance for this amount plus the fee.";
  }
  const canReview =
    !!asset &&
    !!target?.usable &&
    !!amount &&
    !amountError &&
    compareDecimalStrings(amount, asset.minWithdrawal) >= 0 &&
    !!total &&
    compareDecimalStrings(total, available) <= 0;

  const submit = useMutation({
    mutationFn: () =>
      api.requestWithdrawal({
        whitelistAddressId: targetId,
        asset: symbol,
        amount: amount!,
        totpCode: code,
        idempotencyKey,
      }),
    onSuccess: async (w) => {
      setResult(w);
      setReviewOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.balances }),
        queryClient.invalidateQueries({ queryKey: qk.withdrawals }),
      ]);
    },
  });

  const openReview = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canReview) return;
    setCode("");
    submit.reset();
    // One key per review: retrying the same review can never create a second withdrawal.
    setIdempotencyKey(crypto.randomUUID());
    setReviewOpen(true);
  };

  if (result) {
    const status = withdrawalStatus(result);
    return (
      <>
        <PageHeader title="Withdraw" />
        <Card className="mx-auto max-w-lg">
          <div className="flex flex-col items-center gap-2 text-center">
            <SuccessCheck />
            <h2 className="font-heading text-2xl font-black text-fg">Withdrawal requested</h2>
            <p className="text-sm text-muted">{status.help}</p>
          </div>
          <div className="mt-4">
            <Row label="Amount">
              <Amount value={formatAmount(result.amount, 8)} symbol={result.asset} />
            </Row>
            <Row label="Fee">
              <Amount value={result.fee} symbol={result.asset} />
            </Row>
            <Row label="To">
              <Address value={result.toAddress} />
            </Row>
            <Row label="Status">{status.label}</Row>
          </div>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <Link to="/activity" className={buttonClasses("primary")}>
              View activity
            </Link>
            <Button
              variant="secondary"
              onClick={() => {
                setResult(null);
                setAmountInput("");
                setTouched(false);
              }}
            >
              New withdrawal
            </Button>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Withdraw" subtitle="Send to one of your saved addresses. A 2FA code is required." />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
        <Card>
          <form onSubmit={openReview} className="flex flex-col gap-5" noValidate>
            <Field
              label="Asset"
              hint={asset ? `Available: ${formatAmount(available, 8)} ${asset.symbol}` : undefined}
            >
              {(p) => (
                <Select
                  {...p}
                  value={selectedKey}
                  onChange={(e) => {
                    setSelectedKey(e.target.value);
                    setAmountInput("");
                    setTouched(false);
                  }}
                >
                  {assets.map((a) => {
                    const bal =
                      balances.data?.find((b) => b.chain === a.chain && b.asset === a.symbol)?.balance ?? "0";
                    return (
                      <option key={assetKey(a.chain, a.symbol)} value={assetKey(a.chain, a.symbol)}>
                        {a.symbol} · {networkLabel(findChain(chains, a.chain), a.chain)} —{" "}
                        {formatAmount(bal, 6)}
                      </option>
                    );
                  })}
                </Select>
              )}
            </Field>

            <fieldset className="flex min-w-0 flex-col gap-2">
              <legend className="mb-1.5 text-sm font-medium text-fg">To address</legend>
              {chain && (
                <p className="-mt-1 text-xs text-muted">Only {networkLabel(chain)} addresses are shown.</p>
              )}
              {targets.length === 0 && !whitelist.isPending && (
                <Notice tone="info">
                  You have no saved {chain ? networkLabel(chain) : ""} address yet. Add one to continue.
                </Notice>
              )}
              <div role="radiogroup" aria-label="Saved addresses" className="flex flex-col gap-2">
                {targets.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={t.id === targetId}
                    disabled={!t.usable}
                    onClick={() => setTargetId(t.id)}
                    className={cn(
                      "flex min-h-14 flex-col items-start gap-0.5 rounded-field border px-3 py-2 text-left transition-colors duration-150 disabled:opacity-60",
                      t.id === targetId
                        ? "border-primary bg-primary-soft ring-1 ring-primary"
                        : "border-line bg-surface hover:bg-hover",
                    )}
                  >
                    <span className="text-sm font-medium text-fg">{t.label || "Saved address"}</span>
                    <span className="w-full truncate font-mono text-xs text-muted" title={t.address}>
                      {t.address}
                    </span>
                    {!t.usable && (
                      <span className="text-xs text-warning">
                        Usable from {new Date(t.activeFrom).toLocaleString()}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="self-start" onClick={() => setAddOpen(true)}>
                <Plus className="size-4" aria-hidden /> Add address
              </Button>
            </fieldset>

            <Field
              label="Amount"
              error={amountError}
              hint={
                asset
                  ? `Minimum ${asset.minWithdrawal} ${asset.symbol}. Fee ${fee} ${asset.symbol} is added on top.`
                  : undefined
              }
            >
              {(p) => (
                <div className="flex gap-2">
                  <Input
                    {...p}
                    value={amountInput}
                    onChange={(e) => setAmountInput(e.target.value)}
                    onBlur={() => setTouched(true)}
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="0.00"
                    className="font-mono"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setAmountInput(max);
                      setTouched(true);
                    }}
                    disabled={!asset || isZeroAmount(max)}
                  >
                    Max
                  </Button>
                </div>
              )}
            </Field>

            <Button type="submit" block disabled={!asset || !target}>
              Review withdrawal
            </Button>
          </form>
        </Card>

        <Card>
          <CardTitle className="mb-2">Summary</CardTitle>
          <Row label="Network">{chain ? networkLabel(chain) : "—"}</Row>
          <Row label="You send">
            {amount && asset ? <Amount value={amount} symbol={asset.symbol} /> : "—"}
          </Row>
          <Row label="Fee">{asset ? <Amount value={fee} symbol={asset.symbol} /> : "—"}</Row>
          <Row label="Total from balance" strong>
            {total && asset ? <Amount value={total} symbol={asset.symbol} /> : "—"}
          </Row>
          <p className="mt-3 text-xs text-muted">
            Larger withdrawals may need an admin's approval first. Withdrawals can be paused for safety.
          </p>
        </Card>
      </div>

      <Modal
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        title="Confirm withdrawal"
        description="Check every detail. Crypto transfers can't be reversed."
      >
        {asset && target && amount && total && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (code.length === 6) submit.mutate();
            }}
            className="flex flex-col gap-4"
          >
            <div className="rounded-field border border-line bg-soft p-3">
              <Row label="Network">{networkLabel(chain)}</Row>
              <Row label="To">
                <span className="font-mono text-xs break-all" data-testid="review-address">
                  {target.address}
                </span>
              </Row>
              <Row label="Recipient gets">
                <Amount value={amount} symbol={asset.symbol} />
              </Row>
              <Row label="Fee">
                <Amount value={fee} symbol={asset.symbol} />
              </Row>
              <Row label="Total from balance" strong>
                <Amount value={total} symbol={asset.symbol} />
              </Row>
            </div>
            <Field label="2FA code" hint="Each code works once. If you just used one, wait for the next.">
              {(p) => <CodeInput {...p} value={code} onChange={setCode} autoFocus />}
            </Field>
            {submit.error && <Notice tone="danger">{errorMessage(submit.error)}</Notice>}
            <Button type="submit" block loading={submit.isPending} disabled={code.length !== 6}>
              Confirm and withdraw
            </Button>
          </form>
        )}
      </Modal>

      <AddAddressModal
        open={addOpen}
        onOpenChange={setAddOpen}
        defaultChain={chainKey}
        onAdded={(entry) => setTargetId(entry.id)}
      />
    </>
  );
}
